import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Leva } from "leva";
import Logo from "../../assets/images/logo.png";
import useCustomizerStore from "../../state/customizer";
import useTransitionStore from "../../state/transitions";
import { defaultControlAngles } from "../../util/defaultControlAngles";
import { fetchCustomizerData } from "../../util/customizerData";
import FlatIntroSequence from "./FlatIntroSequence";
import FlatTrimSelection from "./FlatTrimSelection";
import ShowroomCanvas from "./ShowroomCanvas";
import "./experience.css";

const EXPERIENCE_STAGES = {
  intro: "intro",
  trims: "trims",
  showroom: "showroom",
};

export default function WebXRExperience() {
  const [stage, setStage] = useState(EXPERIENCE_STAGES.intro);
  const transitionSnapshotRef = useRef(null);
  const model = useCustomizerStore((state) => state.model);
  const setOrbitMode = useTransitionStore((state) => state.setOrbitMode);
  const setDisplayMode = useTransitionStore((state) => state.setDisplayMode);
  const setDayMode = useTransitionStore((state) => state.setDayMode);
  const setHeaderFixed = useTransitionStore((state) => state.setHeaderFixed);
  const setModelInHeader = useTransitionStore(
    (state) => state.setModelInHeader
  );
  const setCamIndex = useTransitionStore((state) => state.setCamIndex);
  const setShowCar = useTransitionStore((state) => state.setShowCar);
  const setShowInterior = useTransitionStore((state) => state.setShowInterior);
  const setShowHotspots = useTransitionStore((state) => state.setShowHotspots);
  const setCameraSide = useTransitionStore((state) => state.setCameraSide);
  const setHotspotControls = useTransitionStore(
    (state) => state.setHotspotControls
  );
  const setAzimuthAndPolarAngles = useTransitionStore(
    (state) => state.setAzimuthAndPolarAngles
  );
  const setSuspensionMode = useTransitionStore(
    (state) => state.setSuspensionMode
  );
  const trimData = useMemo(() => fetchCustomizerData(), []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    transitionSnapshotRef.current = useTransitionStore.getState();

    setOrbitMode(false);
    setDisplayMode("exterior");
    setDayMode("day");
    setHeaderFixed(true);
    setModelInHeader(true);
    setCamIndex(1);
    setShowCar(true);
    setShowInterior(false);
    setShowHotspots(false);
    setCameraSide("passenger");
    setHotspotControls(false);
    setAzimuthAndPolarAngles(defaultControlAngles);
    setSuspensionMode(false);

    return () => {
      document.body.style.overflow = "";

      if (transitionSnapshotRef.current) {
        useTransitionStore.setState(transitionSnapshotRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (stage === EXPERIENCE_STAGES.showroom) {
      setDisplayMode("exterior");
      setOrbitMode(false);
      setModelInHeader(true);
      setCamIndex(1);
      return;
    }

    setDisplayMode("exterior");
    setOrbitMode(false);
    setShowHotspots(false);
    setCamIndex(1);
  }, [stage]);

  const currentTrim = trimData[model];

  return (
    <ExperienceRoot>
      <Leva hidden />
      <ExperienceHeader>
        <img src={Logo} alt="Toyota" />
        <HeaderCopy>
          <span className="eyebrow">Toyota Crown WebXR Prototype</span>
          <span className="title">
            {stage === EXPERIENCE_STAGES.intro
              ? "Crown"
              : currentTrim.label.toUpperCase()}
          </span>
        </HeaderCopy>
      </ExperienceHeader>

      {stage === EXPERIENCE_STAGES.intro && (
        <FlatIntroSequence
          onExplore={() => setStage(EXPERIENCE_STAGES.trims)}
        />
      )}

      {stage === EXPERIENCE_STAGES.trims && (
        <FlatTrimSelection
          currentTrim={model}
          trims={trimData}
          onBack={() => setStage(EXPERIENCE_STAGES.intro)}
          onSelectTrim={() => setStage(EXPERIENCE_STAGES.showroom)}
          onTrimChange={setModel}
        />
      )}

      {stage === EXPERIENCE_STAGES.showroom && (
        <>
          <ShowroomCanvas
            onBack={() => setStage(EXPERIENCE_STAGES.trims)}
            trimLabel={currentTrim.label}
          />
        </>
      )}
    </ExperienceRoot>
  );
}

const ExperienceRoot = styled.main`
  position: relative;
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(88, 88, 88, 0.16), transparent 38%),
    linear-gradient(180deg, #101010 0%, #040404 100%);
  color: #f5f3ef;
  overflow: hidden;
`;

const ExperienceHeader = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;

  img {
    width: 150px;
    height: auto;
  }

  @media (max-width: 768px) {
    padding: 18px 18px 12px;

    img {
      width: 110px;
    }
  }
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;

  .eyebrow {
    font-size: 0.7rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(245, 243, 239, 0.68);
  }

  .title {
    font-size: 1rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  @media (max-width: 768px) {
    .eyebrow {
      font-size: 0.58rem;
    }

    .title {
      font-size: 0.82rem;
    }
  }
`;
